import {Injectable} from '@angular/core';
import {isReadable, mostReadable, TinyColor} from "@ctrl/tinycolor";

export interface Color {
    name: string;
    hex: string;
    darkContrast: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    primaryColorPalette: Color[] = [];
    accentColorPalette: Color[] = [];

    // Service to generate css variables from 2 colours for use in the theme.scss for angular themeing
    savePrimaryColor(color: string): void {
        this.primaryColorPalette = this.computeColors(color);
        document.documentElement.style.setProperty(
            `--primary`, color
        );
        let metaTheme: Element | null = document.querySelector('meta[name="theme-color"]')
        if (metaTheme) {
            metaTheme.setAttribute('content', color)
        }
        let metaThemeTile: Element | null = document.querySelector('meta[name="msapplication-TileColor"]')
        if (metaThemeTile) {
            metaThemeTile.setAttribute('content', color)
        }
        let metaStatusBar: Element | null = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
        if (metaStatusBar) {
            metaStatusBar.setAttribute('content', color)
        }
        document.documentElement.style.setProperty(
            `--primary-text`, this.getReadableColor(color)
        );
        this.updateTheme(this.primaryColorPalette, 'primary');
    }

    saveAccentColor(color: string): void {
        this.accentColorPalette = this.computeColors(color);
        document.documentElement.style.setProperty(
            `--accent`, color
        );
        document.documentElement.style.setProperty(
            `--accent-text`, this.getReadableColor(color)
        );

        this.updateTheme(this.accentColorPalette, 'accent');
    }

    getReadableColor(color: string): string {
        let fallback: string = new TinyColor(color).isLight() ? '#000000' : '#ffffff';
        // Only ever pair the brand colour with pure black or white text, and require
        // WCAG AA for normal text (4.5:1) rather than the weaker AAA-large check.
        let mostReadableColor: string | undefined = mostReadable(color, ["#ffffff", "#000000"], {includeFallbackColors: true})?.toHexString()
        if (!mostReadableColor) {
            mostReadableColor = fallback;
        }
        if (!isReadable(color, mostReadableColor, {level: "AA", size: "small"})) {
            alert('Please choose another colour palette: ' + color + ' does not meet the WCAG AA 4.5:1 contrast requirement against black or white text.')
        }
        return mostReadableColor
    }


    updateTheme(colors: Color[], theme: string): void {
        colors.forEach(color => {
            document.documentElement.style.setProperty(
                `--theme-${theme}-${color.name}`,
                color.hex
            );
            document.documentElement.style.setProperty(
                `--theme-${theme}-contrast-${color.name}`,
                this.getReadableColor(color.hex)
            );
        });
    }

    computeColors(hex: string): Color[] {
        return [
            this.getColorObject(new TinyColor(hex).lighten(52), '50'),
            this.getColorObject(new TinyColor(hex).lighten(37), '100'),
            this.getColorObject(new TinyColor(hex).lighten(26), '200'),
            this.getColorObject(new TinyColor(hex).lighten(12), '300'),
            this.getColorObject(new TinyColor(hex).lighten(6), '400'),
            this.getColorObject(new TinyColor(hex), '500'),
            this.getColorObject(new TinyColor(hex).darken(6), '600'),
            this.getColorObject(new TinyColor(hex).darken(12), '700'),
            this.getColorObject(new TinyColor(hex).darken(18), '800'),
            this.getColorObject(new TinyColor(hex).darken(24), '900'),
            this.getColorObject(new TinyColor(hex).lighten(50).saturate(30), 'A100'),
            this.getColorObject(new TinyColor(hex).lighten(30).saturate(30), 'A200'),
            this.getColorObject(new TinyColor(hex).lighten(10).saturate(15), 'A400'),
            this.getColorObject(new TinyColor(hex).lighten(5).saturate(5), 'A700')
        ];
    }

    getColorObject(value: any, name: string): Color {
        const c: TinyColor = new TinyColor(value);
        return {
            name: name,
            hex: c.toHexString(),
            darkContrast: c.isLight()
        };
    }
}
